import React, { useState } from 'react';
import { colors, typography, spacing, borderRadius } from '../tokens';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

export interface AlertProps {
  variant: AlertVariant;
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
}

/**
 * WCAG AA contrast (body text on bg):
 *   success : #1B5E20 on #E8F5E9 = 6.2:1
 *   error   : #B71C1C on #FFEBEE = 5.5:1
 *   warning : #7A5A00 on #FFF8E1 = 6.1:1
 *   info    : #0D47A1 on #E3F2FD = 6.8:1
 */
type AlertConfig = { bg: string; color: string; border: string; icon: string; role: 'alert' | 'status' };

function getConfig(variant: AlertVariant): AlertConfig {
  switch (variant) {
    case 'success': return { bg: colors.semantic.successBg, color: colors.semantic.successDark, border: colors.semantic.success, icon: '✓', role: 'status' };
    case 'error':   return { bg: colors.semantic.errorBg,   color: colors.semantic.errorDark,   border: colors.semantic.error,   icon: '✕', role: 'alert' };
    case 'warning': return { bg: colors.semantic.warningBg, color: colors.semantic.warningDark, border: colors.semantic.warning,  icon: '!', role: 'alert' };
    case 'info':    return { bg: colors.semantic.infoBg,    color: colors.semantic.infoDark,    border: colors.semantic.info,    icon: 'ℹ', role: 'status' };
  }
}

export function Alert({ variant, title, children, dismissible = false, onDismiss }: AlertProps) {
  const [dismissed, setDismissed] = useState(false);
  const c = getConfig(variant);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div
      role={c.role}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing.sm,
        padding: spacing.md,
        borderRadius: borderRadius.md,
        borderLeft: `4px solid ${c.border}`,
        backgroundColor: c.bg,
        color: c.color,
        fontFamily: typography.fontFamily.body,
        fontSize: typography.fontSize.base,
        lineHeight: typography.lineHeight.normal,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      <span aria-hidden style={{ flexShrink: 0, fontWeight: typography.fontWeight.bold, marginTop: '1px' }}>
        {c.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontWeight: typography.fontWeight.semibold, marginBottom: children ? spacing.xs : 0 }}>
            {title}
          </div>
        )}
        {children}
      </div>
      {dismissible && (
        <button
          type="button"
          aria-label="Tutup amaran"
          onClick={handleDismiss}
          style={{
            flexShrink: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: c.color,
            fontSize: typography.fontSize.lg,
            padding: '0 2px',
            lineHeight: 1,
            opacity: 0.7,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
